import type { Evidence, EvidenceStatus } from '@app-types';
import { getEvidenceStatusLabel } from '@utils/referenceCatalog';

/** Shown wherever Draft Evidence appears (Assessment Detail, review modal). */
export const DRAFT_EVIDENCE_LABEL = 'Draft — awaiting CM submission';

/** Contract Manager-facing wording for Draft Evidence (Evidence Management, CM views). */
export const DRAFT_AWAITING_YOUR_SUBMISSION_LABEL = 'Awaiting your submission';

/** Shown for a Process that needs evidence but has none yet. */
export const EVIDENCE_NEEDED_LABEL = 'Evidence Needed';

/** Shown when a Process holds evidence in more than one terminal state (e.g. approved and rejected). */
export const MIXED_EVIDENCE_LABEL = 'Mixed Status';

export const DRAFT_EVIDENCE_NOT_FOUND_MESSAGE =
  'No draft evidence was found for this process. Please refresh the page.';

/**
 * Upload-button visibility shared by Assessment Detail (ProcessItem) and
 * Evidence Management. Show only when there is no evidence or every item is
 * rejected — hide for draft (awaiting CM submission), pending (Under Review),
 * approved, and mixed.
 */
export function canShowUploadEvidenceButton(status: EvidenceStatus): boolean {
  return status === 'none' || status === 'rejected';
}

/**
 * Keep in sync with backend deriveItemEvidenceStatus. A draft wins over every
 * other status because a Process holds at most one draft and nothing else can
 * be uploaded until it is submitted or deleted.
 */
export function deriveUploadEvidenceStatus(evidence: { status: string }[]): EvidenceStatus {
  if (evidence.length === 0) {
    return 'none';
  }
  if (evidence.some((item) => item.status === 'draft')) {
    return 'draft';
  }
  if (evidence.every((item) => item.status === 'rejected')) {
    return 'rejected';
  }
  if (evidence.some((item) => item.status === 'pending')) {
    return 'pending';
  }
  if (evidence.some((item) => item.status === 'approved')) {
    return 'approved';
  }
  return 'mixed';
}

/**
 * Process-level evidence label shared by Assessment Detail and Evidence Management,
 * so the same Process reads the same on both screens. Draft has its own label
 * (DRAFT_EVIDENCE_LABEL / DRAFT_AWAITING_YOUR_SUBMISSION_LABEL, wording depends on the viewer),
 * so it returns null here.
 */
export function getProcessEvidenceLabel(status: EvidenceStatus): string | null {
  switch (status) {
    case 'none':
      return EVIDENCE_NEEDED_LABEL;
    case 'draft':
      return null;
    case 'mixed':
      return MIXED_EVIDENCE_LABEL;
    default:
      return getEvidenceStatusLabel(status);
  }
}

/** A Process holds at most one draft (ADR 0002). */
export function findDraftEvidence(evidence: Evidence[]): Evidence | undefined {
  return evidence.find((item) => item.status === 'draft');
}
